'use client';

import { FC, useEffect, useState } from 'react';
import {
  CtaFeatureSlice,
  CtaFeatureSliceCenteredButton,
  CtaFeatureSliceDefault,
  CtaFeatureSliceDefaultItem,
  CtaFeatureSliceImageWithLogoAndButton,
  CtaFeatureSliceImageWithLogoAndButtonItem,
  CtaFeatureSliceImageWithTitleAndButton,
  CtaFeatureSliceImageWithTwoCta,
  CtaFeatureSliceTitleWithCta,
  CtaFeatureSliceTitleWithTwoLinks,
} from '@/types';
import * as prismicH from '@prismicio/helpers';

import {
  convertHexadecimalFormatToRGB,
  ctaFeatureCenteredButtonFields,
  ctaFeatureImageTitleButtonFields,
  ctaFeatureItemPrimaryFields,
  ctaFeatureSecondaryFields,
  ctaFeatureTitleWithCtaFields,
  htmlSerializerWithProps,
  isNullish,
  toCtaButtonPropsFromItem,
} from '@/utils';
import type { CtaVariant, SliceCtaFieldMap } from '@/utils';

import {
  Container,
  CtaActionsRow,
  CtaButton,
  IconSvg,
  Link,
  PrismicImageWithAlt,
} from '@/components';
import classNames from 'classnames';
import { PrismicLink, PrismicRichText } from '@prismicio/react';
import { EmptyImageFieldImage, FilledImageFieldImage } from '@prismicio/client';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as ISwiper } from 'swiper/types';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

import styles from './CtaFeature.module.scss';

const renderSliceCtaButton = <TSource extends object>(
  source: TSource,
  fields: SliceCtaFieldMap<TSource>,
  fallback: CtaVariant,
  options?: {
    className?: string;
    /** Slice class applied only when Prismic `fit_cta_button_width_to_content` is on. */
    fitClassName?: string;
    /** Override fit flag when it lives on slice primary, not the mapped item. */
    fitContent?: boolean;
  },
) => {
  const cta = toCtaButtonPropsFromItem(source, fields, fallback);

  if (!cta) {
    return null;
  }

  const { fitContent: mappedFitContent, ...ctaProps } = cta;
  const shouldFitContent = options?.fitContent ?? mappedFitContent;

  return (
    <CtaButton
      {...ctaProps}
      className={classNames(
        options?.className,
        shouldFitContent && options?.fitClassName,
      )}
      {...(shouldFitContent && { fitContent: true })}
    />
  );
};

const RenderCta: FC<CtaFeatureSlice> = slice => {
  if (slice.variation === 'titleWithTwoLinks') {
    return <TitleWithTwoLinksVariation slice={slice} />;
  }

  if (slice.variation === 'titleWithCta') {
    return <TitleWithCtaVariation slice={slice} />;
  }

  if (slice.variation === 'centeredButton') {
    return <CenteredButtonVariation slice={slice} />;
  }

  if (slice.variation === 'imageWithTwoCta') {
    return <ImageWithTwoCtaVariation slice={slice} />;
  }

  if (slice.variation === 'imageWithTitleAndButton') {
    return <ImageWithTitleAndButtonVariation slice={slice} />;
  }

  if (slice.variation === 'imageWithLogoAndButton') {
    return <ImageWithLogoAndButtonVariation slice={slice} />;
  }

  return null;
};

const DefaultVariation: FC<{
  slice: CtaFeatureSliceDefault & CtaFeatureSlice;
}> = ({ slice }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState<number | null>(
    null,
  );
  const ctaItems = slice?.items ?? [];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isSlideVisible = (index: number) =>
    (!isMounted && index === 0) || (isMounted && !isNullish(currentSlideIndex));

  const handleSwiperInit = (swiper: ISwiper) => {
    setCurrentSlideIndex(swiper.realIndex);
  };

  const handleSlideChange = (swiper: ISwiper) => {
    setCurrentSlideIndex(swiper.realIndex);
  };

  if (!ctaItems.length) {
    return null;
  }

  const primary = slice?.primary;
  const autoplayConfig = {
    delay: 5000,
    disableOnInteraction: true,
    stopOnLastSlide: false,
  };
  const useAutoPlay = primary?.enable_autoplay ? autoplayConfig : false;
  const fitContentWidth = primary?.fit_cta_button_width_to_content;

  const CTAContent = ({ item }: { item: CtaFeatureSliceDefaultItem }) => {
    const maxWidthHeader = item?.max_width_header
      ? `${item?.max_width_header}px`
      : '100%';
    const maxWidthBlurb = item?.max_width_blurb
      ? `${item?.max_width_blurb}px`
      : '100%';
    const borderRadius = primary?.cta_full_width
      ? '0px'
      : `${primary?.image_border_radius}px`;

    const background = item?.content_background_color
      ? convertHexadecimalFormatToRGB(item?.content_background_color)
      : null;

    const ctaBanner = (
      <>
        <figure
          className={classNames(styles.banner__horizontal, {
            [styles.banner__horizontal_not_placeholder]: item?.show_content,
          })}
          style={{
            borderRadius,
            paddingTop: getApectRatio(
              item?.image?.dimensions?.width as number,
              item?.image?.dimensions?.height as number,
              primary?.percentage_respect_width as number,
              false,
            ),
          }}
        >
          {<RenderImage borderRadius={borderRadius} fill image={item?.image} />}
        </figure>
        <figure
          className={classNames(styles.banner__vertical, {
            [styles.banner__vertical_not_placeholder]: item?.show_content,
          })}
          style={{
            paddingTop: getApectRatio(
              item?.image?.mobile?.dimensions?.width as number,
              item?.image?.mobile?.dimensions?.height as number,
              primary?.percentage_respect_to_width_mobile as number,
              true,
            ),
          }}
        >
          {<RenderImage borderRadius={'0px'} fill image={item?.image.mobile} />}
        </figure>
      </>
    );

    return (
      <>
        {item?.show_content && (
          <Container className={styles.info_container}>
            <div
              className={classNames(styles.info, {
                [styles.info__text_mobile_left_aligned]:
                  item?.text_mobile_aligned_left,
                [styles.info_align_left]: item.content_align === 'left',
                [styles.info_align_right]: item.content_align === 'right',
                [styles.info_align_center]:
                  item.content_align === 'center' || !item.content_align,
                [styles.info_full_width]: item?.max_width_header,
              })}
              style={{
                backgroundColor: item?.content_background_color
                  ? `rgba(${background?.r}, ${background?.g}, ${
                      background?.b
                    }, ${item?.content_background_opacity ?? 1})`
                  : undefined,
              }}
            >
              {renderTextSlice(
                item?.header,
                item?.header_color ?? '',
                maxWidthHeader,
              )}
              <div className={styles.blurb_container}>
                {renderTextSlice(
                  item?.blurb,
                  item?.blurb_color ?? '',
                  maxWidthBlurb,
                )}
              </div>
              <CtaActionsRow
                className={classNames(styles.info__cta_actions, {
                  [styles.info__cta_actions_fit]: fitContentWidth,
                })}
              >
                {renderSliceCtaButton(
                  item,
                  ctaFeatureItemPrimaryFields,
                  'primary',
                  {
                    className: styles.cta__action_button,
                    fitContent: fitContentWidth,
                  },
                )}
                {renderSliceCtaButton(
                  item,
                  ctaFeatureSecondaryFields,
                  'secondary',
                  {
                    className: styles.cta__action_button,
                    fitContent: fitContentWidth,
                  },
                )}
              </CtaActionsRow>
            </div>
          </Container>
        )}
        {(item?.link || item?.external_link?.link_type === 'Any') && (
          <Link
            aria-label={item?.external_link?.text || 'Go to the promotion link'}
            className={styles.cta__image_link}
            href={item?.link || '/'}
          >
            {ctaBanner}
            {item?.apply_effect_to_cta && item?.image?.url && (
              <div className={styles.cta__overlay} />
            )}
          </Link>
        )}
        {!item?.link && item?.external_link?.link_type !== 'Any' && (
          <PrismicLink
            aria-label={item?.external_link?.text || 'Go to the promotion link'}
            className={styles.cta__image_link}
            field={item?.external_link}
          >
            {ctaBanner}
            {item?.apply_effect_to_cta && item?.image?.url && (
              <div className={styles.cta__overlay} />
            )}
          </PrismicLink>
        )}
      </>
    );
  };

  return (
    <Swiper
      autoplay={useAutoPlay}
      className={styles.swipper__cta_carousel}
      effect="fade"
      loop={ctaItems.length > 1}
      modules={[Autoplay, EffectFade, Navigation, Pagination]}
      onAfterInit={handleSwiperInit}
      onSlideChange={handleSlideChange}
      pagination={{
        clickable: true,
        renderBullet: (index, className) =>
          `<span class="${className}" role="button" tabindex="0" aria-label="Go to slide ${
            index + 1
          }"></span>`,
      }}
      slidesPerView={1}
    >
      {ctaItems.map((cta, index) => (
        <SwiperSlide key={index}>
          {isSlideVisible(index) && <CTAContent item={cta} />}
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const CenteredButtonVariation: FC<{
  slice: CtaFeatureSliceCenteredButton & CtaFeatureSlice;
}> = ({ slice }) =>
  renderSliceCtaButton(
    slice.primary ?? {},
    ctaFeatureCenteredButtonFields,
    'primary',
    {
      className: styles.cta__action_button,
      fitClassName: styles.cta__action_button_fit,
    },
  );

const ImageWithTwoCtaVariation: FC<{
  slice: CtaFeatureSliceImageWithTwoCta & CtaFeatureSlice;
}> = ({ slice }) => {
  const titleColor = slice?.primary?.title_color;
  const image = slice?.primary?.image;
  const align = slice?.primary?.content_align;

  return (
    <>
      <figure className={styles.image_section}>
        {<RenderImage image={image} />}
      </figure>
      <div
        className={classNames(styles.content_section, {
          [styles.content_section__align_left]: align === 'left',
        })}
      >
        <PrismicRichText
          components={htmlSerializerWithProps({ color: titleColor })}
          field={slice?.primary?.title}
        />
        <div className={styles.cta_section}>
          {renderCta(
            slice?.primary?.first_cta_link,
            slice?.primary?.first_cta_image,
          )}
          {renderCta(
            slice?.primary?.second_cta_link,
            slice?.primary?.second_cta_image,
          )}
        </div>
      </div>
    </>
  );
};

const TitleWithCtaVariation: FC<{
  slice: CtaFeatureSliceTitleWithCta & CtaFeatureSlice;
}> = ({ slice }) => {
  const { title_color, title } = slice?.primary ?? {};

  return (
    <>
      <div className={styles.title}>
        <PrismicRichText
          components={htmlSerializerWithProps({ color: title_color })}
          field={title}
        />
      </div>
      {renderSliceCtaButton(
        slice.primary ?? {},
        ctaFeatureTitleWithCtaFields,
        'primary',
        { className: styles.cta__button },
      )}
    </>
  );
};

const TitleWithTwoLinksVariation: FC<{
  slice: CtaFeatureSliceTitleWithTwoLinks & CtaFeatureSlice;
}> = ({ slice }) => {
  const titleColor = slice?.primary?.title_color;
  const descriptionColor = slice?.primary?.description_color;

  return (
    <div className={styles.title_two_link_variation_body}>
      <div className={styles.info}>
        <PrismicRichText
          components={htmlSerializerWithProps({ color: titleColor })}
          field={slice?.primary?.title}
        />
        <PrismicRichText
          components={htmlSerializerWithProps({ color: descriptionColor })}
          field={slice?.primary?.description}
        />
      </div>
      <div className={styles.cta_section}>
        {renderCta(
          slice?.primary?.first_cta_link,
          slice?.primary?.first_cta_image,
        )}
        {renderCta(
          slice?.primary?.second_cta_link,
          slice?.primary?.second_cta_image,
        )}
      </div>
    </div>
  );
};

const ImageWithTitleAndButtonVariation: FC<{
  slice: CtaFeatureSliceImageWithTitleAndButton & CtaFeatureSlice;
}> = ({ slice }) => {
  const { title_color, image, content_align, title } = slice?.primary ?? {};

  return (
    <div
      className={classNames(styles.image_title_button_variation_body, {
        [styles.image_title_button_variation_body__align_right]:
          content_align === 'right',
      })}
    >
      <div className={styles.image_container}>
        <figure className={styles.image_wrapper}>
          {<RenderImage image={image} />}
        </figure>
      </div>

      <div
        className={classNames(styles.content_section, {
          [styles.content_section__align_right]: content_align === 'right',
        })}
      >
        <PrismicRichText
          components={htmlSerializerWithProps({ color: title_color })}
          field={title}
        />
        {renderSliceCtaButton(
          slice.primary ?? {},
          ctaFeatureImageTitleButtonFields,
          'primary',
          { className: styles.cta__button },
        )}
      </div>
    </div>
  );
};

const ImageWithLogoAndButtonVariation: FC<{
  slice: CtaFeatureSliceImageWithLogoAndButton & CtaFeatureSlice;
}> = ({ slice }) => {
  const [isMounted, setIsMounted] = useState(false);
  const ctaItems = slice?.items ?? [];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const isSlideVisible = (index: number) =>
    (!isMounted && index === 0) || isMounted;

  if (!ctaItems.length) {
    return null;
  }

  const primary = slice?.primary;
  const ctaFitContentWidth = Boolean(primary?.fit_cta_button_width_to_content);

  const LogoCTAContent = ({
    item,
  }: {
    item: CtaFeatureSliceImageWithLogoAndButtonItem;
  }) => {
    const maxWidthHeader = item?.max_width_header
      ? `${item?.max_width_header}px`
      : '100%';
    const borderRadius = primary?.cta_full_width
      ? '0px'
      : `${primary?.image_border_radius}px`;

    const background = item?.content_background_color
      ? convertHexadecimalFormatToRGB(item?.content_background_color)
      : null;

    const logoSize = item?.logo_size ? `${item?.logo_size}px` : 80;

    const ctaBanner = (
      <>
        <figure
          className={classNames(styles.banner__horizontal, {
            [styles.banner__horizontal_not_placeholder]: item?.show_content,
          })}
          style={{
            borderRadius,
            paddingTop: getApectRatio(
              item?.image?.dimensions?.width as number,
              item?.image?.dimensions?.height as number,
              primary?.percentage_respect_width as number,
              false,
            ),
          }}
        >
          {<RenderImage borderRadius={borderRadius} fill image={item?.image} />}
        </figure>
        <figure
          className={classNames(styles.banner__vertical, {
            [styles.banner__vertical_not_placeholder]: item?.show_content,
          })}
          style={{
            paddingTop: getApectRatio(
              item?.image?.mobile?.dimensions?.width as number,
              item?.image?.mobile?.dimensions?.height as number,
              primary?.percentage_respect_to_width_mobile as number,
              true,
            ),
          }}
        >
          {<RenderImage borderRadius={'0px'} fill image={item?.image.mobile} />}
        </figure>
      </>
    );

    return (
      <>
        {item?.show_content && (
          <Container className={styles.info_container}>
            <div
              className={classNames(styles.info, {
                [styles.info__text_mobile_left_aligned]:
                  item?.text_mobile_aligned_left,
                [styles.info_align_left]: item.content_align === 'left',
                [styles.info_align_right]: item.content_align === 'right',
                [styles.info_align_center]:
                  item.content_align === 'center' || !item.content_align,
                [styles.info_use_cta_link]:
                  !item?.cta_text && (item?.link || item?.external_link),
                [styles.info_full_width]: item?.max_width_header,
              })}
              style={{
                backgroundColor: item?.content_background_color
                  ? `rgba(${background?.r}, ${background?.g}, ${
                      background?.b
                    }, ${item?.content_background_opacity ?? 1})`
                  : undefined,
              }}
            >
              {item?.logo && (
                <div
                  className={styles.logo_container}
                  style={{ width: logoSize }}
                >
                  <PrismicImageWithAlt
                    defaultAlt="Logo"
                    field={item.logo}
                    height={item?.logo?.dimensions?.height}
                    quality={100}
                    style={{
                      objectFit: 'contain',
                      width: '100%',
                      height: 'auto',
                    }}
                    width={item?.logo?.dimensions?.width}
                  />
                </div>
              )}
              {renderTextSlice(
                item?.header,
                item?.header_color ?? '',
                maxWidthHeader,
              )}
              <CtaActionsRow className={styles.info__cta_actions}>
                {renderSliceCtaButton(
                  item,
                  ctaFeatureItemPrimaryFields,
                  'primary',
                  {
                    className: styles.cta__action_button,
                    fitContent: ctaFitContentWidth,
                  },
                )}
                {renderSliceCtaButton(
                  item,
                  ctaFeatureSecondaryFields,
                  'secondary',
                  {
                    className: styles.cta__action_button,
                    fitContent: ctaFitContentWidth,
                  },
                )}
              </CtaActionsRow>
            </div>
          </Container>
        )}
        {(item?.link || item?.external_link?.link_type === 'Any') && (
          <Link
            aria-label={
              item?.cta_text ? `Go to ${item.cta_text}` : 'Go to link'
            }
            className={styles.cta__image_link}
            href={item?.link || '/'}
          >
            {ctaBanner}
            {item?.apply_effect_to_cta && item?.image?.url && (
              <div className={styles.cta__overlay} />
            )}
          </Link>
        )}
        {!item?.link && item?.external_link?.link_type !== 'Any' && (
          <PrismicLink
            aria-label={
              item?.cta_text ? `Go to ${item.cta_text}` : 'Go to link'
            }
            className={styles.cta__image_link}
            field={item?.external_link}
          >
            {ctaBanner}
            {item?.apply_effect_to_cta && item?.image?.url && (
              <div className={styles.cta__overlay} />
            )}
          </PrismicLink>
        )}
      </>
    );
  };

  return (
    <Swiper
      className={styles.swipper__cta_carousel}
      effect="fade"
      loop={ctaItems.length > 1}
      modules={[Autoplay, EffectFade, Navigation, Pagination]}
      pagination={{
        clickable: true,
        renderBullet: (index, className) =>
          `<span class="${className}" role="button" tabindex="0" aria-label="Go to slide ${
            index + 1
          }"></span>`,
      }}
      slidesPerView={1}
    >
      {ctaItems.map((item, index) => (
        <SwiperSlide key={index}>
          {isSlideVisible(index) && <LogoCTAContent item={item} />}
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const getApectRatio = (
  width: number,
  height: number,
  percentage: number,
  mobile: boolean,
) => {
  if (
    !percentage ||
    (mobile && percentage <= 58.32) ||
    (!mobile && percentage <= 20.42)
  ) {
    return undefined;
  }

  const aspectRatioImage = (height / width) * 100;
  const aspectRatioPercentage = (percentage * aspectRatioImage) / 100;

  return `${aspectRatioPercentage}%`;
};

const RenderImage = ({
  image,
  borderRadius = '0px',
  fill,
}: {
  image: EmptyImageFieldImage | FilledImageFieldImage;
  borderRadius?: string;
  fill?: boolean;
}) => {
  if (!image?.url) {
    return <IconSvg name="image" />;
  }

  if (fill) {
    return (
      <PrismicImageWithAlt
        field={image}
        fill
        quality={100}
        style={{ borderRadius, objectFit: 'cover', objectPosition: 'center' }}
      />
    );
  }

  return (
    <PrismicImageWithAlt
      field={image}
      height={image?.dimensions?.height}
      quality={100}
      style={{
        borderRadius: borderRadius,
        objectFit: 'contain',
      }}
      width={image?.dimensions?.width}
    />
  );
};

const renderTextSlice = (text: any, color: string, maxWidth?: string) => {
  if (!prismicH.asText(text)) {
    return;
  }

  return (
    <PrismicRichText
      components={htmlSerializerWithProps({
        color: color,
        maxWidth: maxWidth,
      })}
      field={text}
    />
  );
};

function renderCta(ctaLink, ctaImage) {
  if (!ctaImage?.url) {
    return null;
  }

  const image = (
    <PrismicImageWithAlt
      className={styles.cta_logo}
      defaultAlt="Logo"
      field={ctaImage}
      height={ctaImage?.dimensions?.height}
      width={ctaImage?.dimensions?.width}
    />
  );

  if (ctaLink && ctaLink?.url) {
    const href = prismicH.asLink(ctaLink) || '/';

    return (
      <Link aria-label="Go to link" href={href}>
        {image}
      </Link>
    );
  }

  if (ctaLink) {
    return (
      <PrismicLink aria-label="Go to link" field={ctaLink}>
        {image}
      </PrismicLink>
    );
  }

  return image;
}

export { DefaultVariation, RenderCta };
